import { ref } from 'vue'
import * as pdfMake from 'pdfmake/build/pdfmake'
import type { Content, TableCell, TDocumentDefinitions } from 'pdfmake/interfaces'
import { getIps, getHeaderImage } from '@/modules/Core/usings/impresion'
import { getDocumentHeader } from '@/modules/Core/usings/pdf/pdfHeader'
import { getDocumentFooter } from '@/modules/Core/usings/pdf/pdfFooter'
import { getHeaderInfo, type IHeaderPdfData } from '@/modules/Core/usings/pdf/pacienteData'
import { styles, pdfTableLayout } from '@/modules/Core/usings/pdf/pdfStyles'
import { formatearFechaEnEspanol } from '@/modules/Core/usings/generales'
import { alertasService, admisionService } from '@/services'
import type { IAlertAlertas, ICitaTechnologiasSalud } from '@/modules/Asistencial/interfaces'
import type { ICitaAdmision } from '@/modules/Admision/interfaces'
import type { IConfPersonasEmpleado, IConfPersonasPaciente } from '@/modules/Administracion/interfaces'
import type { IConfCups } from '@/modules/Contratacion/interfaces'
import type { ICitaOrdenesTecnologia } from '@/modules/Admision/interfaces/tablas/ICitaOrdenesTecnologia'
import type { IAtencionTraza } from '@/modules/DemandaInducida/interfaces/ITrazabilidadRuta'

export function useTrazabilidadRutaPdf() {
  const generando = ref(false)

  const fecha = (valor?: string | null) => (valor ? formatearFechaEnEspanol(valor) : '-')
  const nombreCups = (cups?: IConfCups | null) => [cups?.cocuCups, cups?.cocuNombre].filter(Boolean).join(' - ')
  const nombreProfesional = (emp?: IConfPersonasEmpleado | null) =>
    [emp?.copnNombre1, emp?.copnNombre2, emp?.copnApellido1, emp?.copnApellido2].filter(Boolean).join(' ') || 'Sin profesional'

  const seccion = (titulo: string, encabezados: string[], filas: TableCell[][], widths: (string | number)[]): Content[] => {
    if (!filas.length) return []
    return [
      { text: titulo, style: 'header', margin: [0, 8, 0, 3] },
      {
        table: {
          headerRows: 1,
          widths,
          body: [encabezados.map((text) => ({ text, style: 'tableHeader' })), ...filas]
        },
        layout: pdfTableLayout,
        margin: [0, 0, 0, 6]
      }
    ]
  }

  const seccionOrdenes = (titulo: string, ordenes: ICitaOrdenesTecnologia[]): Content[] => {
    const filas: TableCell[][] = []
    for (const orden of ordenes) {
      for (const detalle of (orden.detalles || [])) {
        filas.push([
          { text: nombreCups(detalle.cups) || '-', style: 'tableCellSmall' },
          { text: detalle.ciodObservacion || '-', style: 'tableCellSmall' },
          { text: fecha(orden.ciotFechaInicio), style: 'tableCellSmall' }
        ])
      }
    }
    return seccion(titulo, ['CUPS', 'Detalle', 'Fecha'], filas, ['*', '*', 80])
  }

  const construirCabecera = async (admision: ICitaAdmision, tecnologia?: ICitaTechnologiasSalud): Promise<Content[]> => {
    const ips = await getIps()
    const headerImage = await getHeaderImage(tecnologia)
    const rutaPrincipal = tecnologia?.contratoxservicio?.servicio?.coseNombre || tecnologia?.coseNombre || ''

    const dataHeaderPdf: IHeaderPdfData = {
      ...(admision.paciente as IConfPersonasPaciente),
      technologia: tecnologia,
      fechaAtencion: admision.ciadFechaOrigen || tecnologia?.cittFechaInicio || tecnologia?.cittFechaEntrega || undefined,
      horaAtencion: tecnologia?.cittHora,
      numAutorizacion: tecnologia?.cittNumeroAutorizacion,
      contrato: tecnologia?.contratoxservicio?.contrato || admision.contrato
    }
    const tablasPaciente = await getHeaderInfo(dataHeaderPdf, admision)

    return [
      getDocumentHeader(ips, headerImage),
      { text: `TRAZABILIDAD DE ATENCIONES${rutaPrincipal ? ` — ${rutaPrincipal}` : ''}`, style: 'header', alignment: 'center', margin: [0, 0, 0, 6] },
      ...(tablasPaciente as Content[])
    ]
  }

  const encabezadoAtencion = (atencion: ICitaTechnologiasSalud, esRuta: boolean): Content => {
    const servicio = atencion.coseNombre || atencion.cittTechnologia || 'Atención'
    const titulo = [atencion.cittCodTechnologia, servicio].filter(Boolean).join(' - ')
    const meta = [atencion.cittHora || fecha(atencion.cittFechaInicio), nombreProfesional(atencion.empleado)]
      .filter(Boolean)
      .join('  ·  ')
    return {
      table: {
        widths: ['*', 'auto'],
        body: [[
          {
            text: [
              { text: titulo, style: 'subheaderTech' },
              ...(esRuta ? [{ text: '   * ATENCIÓN DE LA RUTA', bold: true, color: '#000000', fontSize: 8 }] : [])
            ]
          },
          { text: meta, style: 'tableCellSmall', alignment: 'right' }
        ]]
      },
      layout: {
        ...pdfTableLayout,
        fillColor: () => (esRuta ? '#d9d9d9' : '#f2f2f2')
      },
      margin: [0, 10, 0, 3]
    }
  }

  const subseccionesAtencion = (a: IAtencionTraza): Content[] => [
    ...seccion(
      'DIAGNÓSTICOS',
      ['Código', 'Descripción', 'Tipo', 'Clase'],
      a.diagnosticos.map((dx) => [
        { text: dx.cie10?.comaNombrecorto || '-', style: 'tableCellSmall' },
        { text: dx.cie10?.comaNombrelargo || '-', style: 'tableCellSmall' },
        { text: dx.diagnostico?.comaNombrecorto || '-', style: 'tableCellSmall' },
        { text: dx.clase?.comaNombrecorto || '-', style: 'tableCellSmall' }
      ]),
      [55, '*', 55, 55]
    ),
    ...seccionOrdenes('ÓRDENES DE LABORATORIO', a.ordenesLaboratorio),
    ...seccionOrdenes('ÓRDENES DE PROCEDIMIENTO', a.ordenesProcedimiento),
    ...seccionOrdenes('REMISIONES', a.ordenesRemision),
    ...seccionOrdenes('OTROS SERVICIOS', a.ordenesOtros),
    ...seccion(
      'RECETARIOS',
      ['Medicamento', 'Unidades', 'Frecuencia', 'Detalle'],
      a.recetarios.map((r) => [
        { text: r.medicamento?.megeNombre || '-', style: 'tableCellSmall' },
        { text: String(r.ciodCantidadprescrita ?? ''), style: 'tableCellSmall', alignment: 'center' },
        { text: `${r.ciodFrecuenciahoras || 0} hrs x ${r.ciodDuraciondias || 0} días -> ${r.ciodCantidaddosis || 0} dosis`, style: 'tableCellSmall', alignment: 'center' },
        { text: r.ciodObservacion || '-', style: 'tableCellSmall' }
      ]),
      ['*', 45, 120, '*']
    ),
    ...seccion(
      'PROCEDIMIENTOS APLICADOS',
      ['Procedimiento', 'Cant.', 'Fecha', 'Observación'],
      a.procedimientosAplicados.map((p) => [
        { text: nombreCups(p.cup) || p.orden?.comaNombrelargo || '-', style: 'tableCellSmall' },
        { text: String(p.ciorCantidad ?? ''), style: 'tableCellSmall', alignment: 'center' },
        { text: fecha(p.ciorFechaprocedimiento || p.ciorFechatoma || p.createdAt), style: 'tableCellSmall' },
        { text: p.ciorObservacion || '-', style: 'tableCellSmall' }
      ]),
      ['*', 35, 80, '*']
    ),
    ...seccion(
      'MEDICAMENTOS APLICADOS',
      ['Medicamento', 'Cant.', 'Fecha', 'Observación'],
      a.medicamentosAplicados.map((m) => [
        { text: m.medicamento?.mediNombre || '-', style: 'tableCellSmall' },
        { text: String(m.cimaCantidad ?? ''), style: 'tableCellSmall', alignment: 'center' },
        { text: fecha(m.cimaFecha), style: 'tableCellSmall' },
        { text: m.cimaDescripcion || '-', style: 'tableCellSmall' }
      ]),
      ['*', 35, 80, '*']
    ),
    ...seccion(
      'INSUMOS APLICADOS',
      ['Insumo', 'Cant.', 'Fecha', 'Observación'],
      a.insumosAplicados.map((i) => [
        { text: i.insumo?.mediNombre || '-', style: 'tableCellSmall' },
        { text: String(i.ciiaCantidad ?? ''), style: 'tableCellSmall', alignment: 'center' },
        { text: fecha(i.ciiaFecha), style: 'tableCellSmall' },
        { text: i.ciiaDescripcion || '-', style: 'tableCellSmall' }
      ]),
      ['*', 35, 80, '*']
    ),
    ...seccion(
      'INCAPACIDADES',
      ['Fecha inicio', 'Fecha fin', 'Días', 'Detalle'],
      a.incapacidades.map((inc) => [
        { text: fecha(inc.ciceFechainicio), style: 'tableCellSmall' },
        { text: fecha(inc.ciceFechafinal), style: 'tableCellSmall' },
        { text: String(inc.ciceDias ?? ''), style: 'tableCellSmall', alignment: 'center' },
        { text: inc.ciceCertificado || '-', style: 'tableCellSmall' }
      ]),
      [80, 80, 40, '*']
    ),
    ...seccion(
      'CERTIFICADOS',
      ['Detalle'],
      a.certificados.map((cert) => [
        { text: cert.ciceCertificado || '-', style: 'tableCellSmall' }
      ]),
      ['*']
    )
  ]

  const construirAtenciones = (atenciones: IAtencionTraza[], cittRuta?: number): Content[] =>
    [...atenciones]
      .sort((a, b) => Number(b.atencion.cittId === cittRuta) - Number(a.atencion.cittId === cittRuta))
      .flatMap((a) => {
        const esRuta = a.atencion.cittId === cittRuta
        const subsecciones = subseccionesAtencion(a)
        return [
          encabezadoAtencion(a.atencion, esRuta),
          ...(subsecciones.length
            ? subsecciones
            : [{ text: 'Sin registros para esta atención.', style: 'tableCellSmall', italics: true, margin: [0, 0, 0, 6] } as Content])
        ]
      })

  const generarPdf = async (alerta: IAlertAlertas) => {
    if (generando.value) return
    if (!alerta.cittId) {
      window.notificacion.notificacion('warning', 'La alerta no tiene una atención asociada.')
      return
    }
    generando.value = true
    window.notificacion.loading().circle('Generando trazabilidad...')
    try {
      const [{ data: admResp }, { data: trazaResp }] = await Promise.all([
        admisionService.getAdmisionByTechId(alerta.cittId, { withPacienteSnapshot: true }),
        alertasService.trazabilidadRuta(alerta.cittId)
      ])
      const admision = admResp.body
      const data = trazaResp.body
      if (!admision || !data || !data.atenciones?.length) {
        window.notificacion.notificacion('warning', 'No se encontraron atenciones realizadas para esta fecha.')
        return
      }
      const tecnologia = (admision.technologias || []).find((t) => t.cittId === alerta.cittId)

      const definicion: TDocumentDefinitions = {
        styles,
        pageMargins: [30, 20, 30, 50],
        content: [...(await construirCabecera(admision, tecnologia)), ...construirAtenciones(data.atenciones, alerta.cittId)],
        footer: getDocumentFooter(),
        info: {
          title: 'Trazabilidad de atenciones',
          keywords: 'trazabilidad, ruta, atención, IPS',
          creator: 'Analuu Tools'
        }
      }
      pdfMake.createPdf(definicion).open()
    } catch (error) {
      console.error('Error generando la trazabilidad de la ruta:', error)
      window.notificacion.notificacion('failure', 'Ocurrió un error al generar el PDF de trazabilidad.')
    } finally {
      window.notificacion.loading().remove()
      generando.value = false
    }
  }

  return { generando, generarPdf }
}
